import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError, del, fetchBlobUrl, get } from '../api/client';
import { isMissing } from '../api/queries';
import type { AutonomyEvent } from '../api/types';
import { useLive } from '../live/store';

/** One autonomy conversation (GET /api/autonomy/sessions). Field names are read tolerantly: the API is new. */
export interface SessionSummary {
  id: string;
  serial: string;
  robotName: string | null;
  profileId: string | null;
  profileName: string | null;
  startedAt: string | null;
  endedAt: string | null;
  durationS: number | null;
  turns: number | null;
  toolCalls: number | null;
  messageCount: number | null;
  endReason: string | null;
  preview: string | null;
  lang: string | null;
  active: boolean;
}
export interface SessionDetail extends SessionSummary {
  messages: AutonomyEvent[];
  /** more messages exist before the first one returned; `nextBefore` = the server's cursor for them */
  hasMore: boolean;
  nextBefore: string | null;
}
export interface SessionFilters {
  serial?: string;
  profileId?: string;
  from?: string;
  to?: string;
  q?: string;
}
export interface SessionPage {
  items: SessionSummary[];
  total: number | null;
  /** cursor for the next page: `{page}` (server pages), a cursor string, or an offset number */
  next: { page: number } | string | number | null;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : typeof v === 'number' ? String(v) : null);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const iso = (v: unknown): string | null => (typeof v === 'string' ? v : typeof v === 'number' ? new Date(v < 1e12 ? v * 1000 : v).toISOString() : null);

export function parseSession(raw: unknown): SessionSummary | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = str(o.id ?? o.sessionId);
  const serial = str(o.serial ?? o.robot ?? o.robotSerial);
  if (!id || !serial) return null;
  const startedAt = iso(o.startedAt ?? o.started ?? o.createdAt ?? o.start);
  const endedAt = iso(o.endedAt ?? o.ended ?? o.finishedAt ?? o.end);
  // prefer the timestamps: the server's durationS has been seen disagreeing with startedAt → endedAt
  let durationS = startedAt && endedAt ? Math.max(0, (Date.parse(endedAt) - Date.parse(startedAt)) / 1000) : num(o.durationS ?? o.duration);
  if (durationS === null && startedAt && Boolean(o.active)) durationS = Math.max(0, (Date.now() - Date.parse(startedAt)) / 1000);
  const counters = (o.counters && typeof o.counters === 'object' ? o.counters : {}) as Record<string, unknown>;
  const profile = o.profile && typeof o.profile === 'object' ? (o.profile as Record<string, unknown>) : null;
  return {
    id,
    serial,
    robotName: str(o.robotName ?? o.name),
    profileId: str(o.profileId ?? profile?.id),
    profileName: str(o.profileName ?? profile?.name),
    startedAt,
    endedAt,
    durationS,
    turns: num(o.turns ?? o.visitorTurns ?? o.utterances ?? counters.turns ?? counters.utterances),
    messageCount: num(o.messageCount ?? o.messages),
    toolCalls: num(o.toolCalls ?? o.tools ?? counters.toolCalls),
    endReason: str(o.endReason ?? o.reason),
    preview: str(typeof o.preview === 'object' && o.preview ? (o.preview as Record<string, unknown>).first : (o.preview ?? o.firstUtterance ?? o.first)),
    lang: str(o.lang),
    active: Boolean(o.active ?? (!endedAt && !o.endReason)),
  };
}

export function parseList(data: unknown): SessionPage {
  const o = (data && typeof data === 'object' && !Array.isArray(data) ? data : {}) as Record<string, unknown>;
  const list = Array.isArray(data) ? data : ((o.items ?? o.sessions ?? o.results) as unknown[] | undefined) ?? [];
  const page = num(o.page);
  const pages = num(o.pages);
  return {
    items: (Array.isArray(list) ? list : []).map(parseSession).filter((x): x is SessionSummary => Boolean(x)),
    total: num(o.total ?? o.count),
    next: page !== null && pages !== null ? (page < pages ? { page: page + 1 } : null) : typeof o.next === 'string' || typeof o.next === 'number' ? o.next : typeof o.nextCursor === 'string' ? o.nextCursor : typeof o.nextOffset === 'number' ? o.nextOffset : null,
  };
}

/** Messages of a session as transcript events (serial filled in; `ts` normalised to ISO). */
export function parseDetail(data: unknown, fallback?: SessionSummary | null): SessionDetail | null {
  const o = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
  const head = parseSession(o.session ?? o.header ?? o) ?? fallback ?? null;
  if (!head) return null;
  const list = (o.messages ?? o.events ?? o.items) as unknown[] | undefined;
  const messages: AutonomyEvent[] = (Array.isArray(list) ? list : [])
    .filter((m): m is Record<string, unknown> => Boolean(m && typeof m === 'object'))
    // {ts, kind, lang, text, data}: the kind-specific fields live in `data` (engine/by, input/result/ok, phase…)
    .map((m) => ({ ...(m.data && typeof m.data === 'object' ? (m.data as object) : {}), ...(m as object), data: undefined, type: 'autonomy', serial: head.serial, sessionId: head.id, kind: String(m.kind ?? m.type ?? 'note'), ts: iso(m.ts ?? m.at ?? m.time) ?? undefined }) as AutonomyEvent);
  return { ...head, messages, hasMore: Boolean(o.hasMore ?? o.more ?? o.nextBefore), nextBefore: str(o.nextBefore ?? o.before) };
}

const PAGE = 50;
export type Cursor = SessionPage['next'];

function qs(params: Record<string, string | number | undefined | null>) {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') u.set(k, String(v));
  const s = u.toString();
  return s ? `?${s}` : '';
}

export async function fetchSessions(f: SessionFilters, cursor: Cursor): Promise<SessionPage> {
  const extra = cursor && typeof cursor === 'object' ? { page: cursor.page } : typeof cursor === 'number' ? { offset: cursor } : cursor ? { cursor } : {};
  const page = parseList(await get<unknown>(`/api/autonomy/sessions${qs({ serial: f.serial, profile: f.profileId, from: f.from, to: f.to, q: f.q, limit: PAGE, ...extra })}`));
  // a server without paging info: paginate by offset while pages come back full
  if (page.next === null && page.items.length === PAGE && !(cursor && typeof cursor === 'object')) page.next = (typeof cursor === 'number' ? cursor : 0) + PAGE;
  return page;
}

export function useSession(id: string | null) {
  return useQuery({
    queryKey: ['autonomy-session', id] as const,
    queryFn: async () => parseDetail(await get<unknown>(`/api/autonomy/sessions/${encodeURIComponent(id!)}${qs({ limit: 200 })}`)),
    enabled: Boolean(id),
    retry: (n, e) => !isMissing(e) && n < 1,
  });
}

export async function fetchOlder(id: string, before: string): Promise<{ messages: AutonomyEvent[]; nextBefore: string | null }> {
  const d = parseDetail(await get<unknown>(`/api/autonomy/sessions/${encodeURIComponent(id)}${qs({ before, limit: 200 })}`));
  return { messages: d?.messages ?? [], nextBefore: d?.hasMore ? (d.nextBefore ?? d.messages[0]?.ts ?? null) : '' };
}

export const deleteSession = (id: string) => del(`/api/autonomy/sessions/${encodeURIComponent(id)}`);

/** Download the export (authenticated GET → blob → anchor). */
export async function exportSession(id: string, format: 'json' | 'txt', fileBase: string) {
  const url = await fetchBlobUrl(`/api/autonomy/sessions/${encodeURIComponent(id)}/export?format=${format}`);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${fileBase}.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * Live transcript: when the page opens with a session already running, load that session's earlier messages
 * once and put them before the live ones. One request per serial+session (shared by every transcript instance and
 * React's double-mount); the result is applied against the store's current log, so nothing is lost on remount.
 * Silently skipped on an older server (404).
 */
const backfilled = new Set<string>();
async function backfill(serial: string, sessionId: string) {
  const key = `${serial}|${sessionId}`;
  if (backfilled.has(key)) return;
  backfilled.add(key);
  try {
    const d = parseDetail(await get<unknown>(`/api/autonomy/sessions/${encodeURIComponent(sessionId)}${qs({ limit: 200 })}`));
    if (!d) return;
    const live = useLive.getState();
    if (live.robots[serial]?.status?.autonomy?.sessionId !== sessionId) return;
    const log = live.autonomyLog[serial] ?? [];
    const first = log[0];
    const firstTs = first ? (first.ts ? Date.parse(first.ts) : first.t) : null;
    const older = d.messages.filter((m) => firstTs === null || !m.ts || Date.parse(m.ts) < firstTs);
    if (older.length) live.prependAutonomy(serial, older);
  } catch (e) {
    if (!(e instanceof ApiError)) throw e;
  }
}

export function useSessionBackfill(serial: string) {
  const sessionId = useLive((s) => s.robots[serial]?.status?.autonomy?.sessionId ?? null);
  const active = useLive((s) => Boolean(s.robots[serial]?.status?.autonomy?.active));
  useEffect(() => {
    if (active && sessionId) void backfill(serial, sessionId);
  }, [active, sessionId, serial]);
}
